import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { WatchlistFilter } from '@sorare/core/src/__generated__/globalTypes';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { TextSwitch } from '@sorare/core/src/atoms/inputs/TextSwitch';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useDebouncedCallback } from '@sorare/core/src/hooks/useDebouncedCallback';
import { glossary, scoutingCenter } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { WatchlistItem } from 'components/watchlists/WatchlistItem';
import { WatchlistsHeader } from 'components/watchlists/WatchlistsHeader';
import { getSubscribableWatchlistTypes } from 'hooks/useHasFollowedPlayers';

import { FavoritesWatchlistItem } from '../FavoritesWatchlistItem';
import { SorareWatchlistBanner } from '../SorareWatchlistBanner';
import { EmptyState } from './EmptyState';
import type {
  WatchlistsPageQuery,
  WatchlistsPageQueryVariables,
} from './__generated__/index.graphql';

const WATCHLISTS_PAGE_QUERY = gql`
  query WatchlistsPageQuery(
    $sport: Sport!
    $query: String!
    $types: [SubscribableType!]
    $filter: WatchlistFilter!
  ) {
    currentUser {
      slug
      myWatchlists(query: $query, sport: $sport, filter: $filter) {
        slug
        id
        ...WatchlistItem_watchlist
      }
      mySubscriptions(types: $types) {
        totalCount
      }
    }
    market {
      id
      sorareWatchlists(sport: $sport) {
        slug
        ...SorareWatchlistBanner_watchlist
      }
    }
  }
  ${WatchlistItem.fragments.watchlist}
  ${SorareWatchlistBanner.fragments.watchlist}
` as TypedDocumentNode<WatchlistsPageQuery, WatchlistsPageQueryVariables>;

const Content = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--triple-unit);
`;
const ItemsContainer = styled(Vertical).attrs({ gap: 1 })`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  column-gap: var(--double-unit);
  row-gap: var(--triple-unit);
`;
const Filter = styled.div`
  grid-column: span 1;
`;
const LastColumn = styled.div`
  width: 100%;
  @media ${tabletAndAbove} {
    max-width: 300px;
  }
`;

const WrapSBHorizontal = styled(SBHorizontal)`
  flex-wrap: wrap;
`;

const WATCHLISTS_FILTERS = [
  {
    label: <FormattedMessage {...glossary.all} />,
    value: WatchlistFilter.ALL_WATCHLISTS,
  },
  {
    label: <FormattedMessage {...scoutingCenter.myWatchlists} />,
    value: WatchlistFilter.OWNED_WATCHLISTS,
  },
  {
    label: <FormattedMessage {...glossary.following} />,
    value: WatchlistFilter.FOLLOWED_WATCHLISTS,
  },
];

type Props = {
  sport: Sport;
};

export const WatchlistsPage = ({ sport }: Props) => {
  const { isMe } = useCurrentUserContext();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState(search);
  const [watchlistsFilter, setWatchlistsFilter] = useState<WatchlistFilter>(
    WatchlistFilter.ALL_WATCHLISTS
  );
  const { formatMessage } = useIntlContext();

  const { data, refetch, previousData } = useQuery(WATCHLISTS_PAGE_QUERY, {
    variables: {
      sport,
      query,
      types: getSubscribableWatchlistTypes(sport),
      filter: watchlistsFilter,
    },
  });

  const debouncedSetQuery = useDebouncedCallback(
    (s: string) => setQuery(s),
    400
  );
  const watchlists = (data || previousData)?.currentUser?.myWatchlists;

  const favoritesCount = data?.currentUser?.mySubscriptions.totalCount || 0;
  const sorareWatchlists = data?.market?.sorareWatchlists || [];
  return (
    <>
      <Vertical>
        <WatchlistsHeader onAddWatchlist={refetch} />
        <SorareWatchlistBanner
          sorareWatchlists={sorareWatchlists}
          withContainer
        />
        <Content>
          <WrapSBHorizontal>
            <Filter>
              <TextSwitch
                variant="segmented"
                value={watchlistsFilter}
                onChange={e => setWatchlistsFilter(e)}
                options={WATCHLISTS_FILTERS}
              />
            </Filter>
            <LastColumn>
              <SearchInput
                value={search}
                small
                onChange={e => {
                  setSearch(e.target.value);
                  debouncedSetQuery(e.target.value);
                }}
                placeholder={formatMessage(glossary.search)}
                withIcon
              />
            </LastColumn>
          </WrapSBHorizontal>
          <ItemsContainer>
            {watchlistsFilter !== WatchlistFilter.FOLLOWED_WATCHLISTS && (
              <FavoritesWatchlistItem count={favoritesCount} />
            )}
            {watchlists?.length ? (
              watchlists?.map(w => (
                <WatchlistItem
                  key={w.slug}
                  watchlist={w}
                  onRemove={refetch}
                  hideOwner={isMe(w.user)}
                  myWatchlists
                />
              ))
            ) : (
              <EmptyState
                onAddWatchlist={refetch}
                search={search}
                watchlistsFilter={watchlistsFilter}
              />
            )}
          </ItemsContainer>
        </Content>
      </Vertical>
    </>
  );
};
